import React from "react";

import { readVerdict } from "../../data/verdict";

/**
 * VerdictBadge — paper 전략의 자동 판정(GO/CONTINUE/NO-GO/DORMANT)을 한 칩으로.
 *
 * 판단 로직은 백엔드(/api/summary 의 verdict)가 단일 소스다. 이 컴포넌트는
 * readVerdict 로 그 값을 읽어 렌더만 한다(백엔드에 없으면 동일 게이트로 폴백).
 *
 * 색상은 DESIGN_SYSTEM §10 EvalBadge 패턴(gain/brand/loss/warning subtle 토큰)을
 * 그대로 따른다 — 3 테마 전환·WCAG AA 보존.
 *
 * Props:
 *   strategy : summary.strategies[i] | families[i] | group | 단일 (verdict 미지정 시 사용)
 *   verdict  : {label, score} 직접 주입(그룹은 families 의 family verdict 를 넘김)
 *   fields   : readStrategyFields 결과(폴백 계산용)
 *   showScore: 게이트 점수(3/5) 노출 여부 (기본 true)
 */
const VERDICT_DEFS = {
    go: { label: "GO", glyph: "●", cls: "bg-gain-subtle text-gain", pulse: false },
    continue: {
        label: "검증중",
        glyph: "◐",
        cls: "bg-brand-subtle text-brand-dark",
        pulse: true,
    },
    nogo: { label: "NO-GO", glyph: "✕", cls: "bg-loss-subtle text-loss", pulse: false },
    dormant: {
        label: "데이터 부족",
        glyph: "◌",
        cls: "bg-surface-elevated text-content-muted border border-surface-border",
        pulse: false,
    },
};

export default function VerdictBadge({ strategy, verdict, fields, showScore = true }) {
    const v = verdict && verdict.label ? verdict : readVerdict(strategy, fields);
    const def = VERDICT_DEFS[v?.label] ?? VERDICT_DEFS.dormant;

    const gateText =
        Array.isArray(v?.gates) && v.gates.length
            ? v.gates
                  .map((g) => `${g.na ? "—" : g.pass ? "✓" : "✕"} ${g.key} (${g.val})`)
                  .join("\n")
            : "";
    const title = `자동 판정: ${def.label}${
        v?.score && v.score !== "—" ? ` · 게이트 ${v.score}` : ""
    }${gateText ? `\n${gateText}` : ""}`;

    return (
        <span
            role="status"
            aria-label={`자동 판정 ${def.label}`}
            title={title}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded shrink-0 ${def.cls}`}
        >
            <span aria-hidden="true" className={def.pulse ? "animate-pulse" : undefined}>
                {def.glyph}
            </span>
            {def.label}
            {showScore && v?.score && v.score !== "—" && (
                <span className="font-normal opacity-70">{v.score}</span>
            )}
        </span>
    );
}
